import React, { useState, useEffect, useContext, useMemo } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import { safeStr, getIPv6Prefix } from "common/api";
import Statistics from "./Statistics";
import ThresholdsSettingsModal from "./ThresholdsSettingsModal";
import ListSubscribers from "./ListSubscribers";
import TimeEvolutionMetrics from "./TimeEvolutionMetrics";
import useFirstTime from "common/hooks/firstTime";
import useDebouncer from "common/hooks/debouncer";

const EmptyChart = styled.div`
  width: 100%;
  height: 50px;
`;

const BOXPLOT_METRICS_SET = new Set([
  "MAX-Mbps",
  "RTT-ms",
  "RTX",
  "CONGEST",
  "MAX-SPEED",
]);

const SUBSCRIBERS_TO_PLOT_LIMIT = 50;

const addCIDR = (subnet) =>
  subnet.includes(":")
    ? /*ipv6*/ getIPv6Prefix().then((prefix) => `${subnet}/${prefix}`)
    : `${subnet}/32`;

const ensureCIDR = (subnet) =>
  Promise.resolve(
    subnet.length === 0 || subnet.includes("/") ? subnet : addCIDR(subnet)
  );

const statePolicy = (policy) =>
  policy === "all" ? "" : `policy ${safeStr(policy)}`;

const stateSubscriber = (subnet) =>
  "subscriber " + (subnet.length === 0 ? "all" : subnet);

const stateSubscriberGroup = (subscriberGroup) =>
  subscriberGroup && subscriberGroup !== "all"
    ? `subscriber-group ${safeStr(subscriberGroup)}`
    : "";

const responseMustContainAtLeastOneRecord = (subnet) => (response) => {
  if (subnet.length > 0) {
    const [_head, ...rows] = response.trim("\n").split("\n");
    if (rows === undefined || rows.length < 1) {
      throw `%ERR-ENOENT: Cannot find subscriber with ip ${subnet}`;
    }
  }

  return { response };
};

const createPlaceholder = ({addr}) => {
    return [
      "ADDR            SUBSCRIBER-ID     BLOCK      FL-ACTIVE FL-CREATED         MBYTES  CURR-Mbps  MEAN-Mbps   MAX-Mbps   LIM-Mbps    RTT-ms      RTX POLICY-RATE              MAX-SPEED  CONGEST   WARN   LIFETIME",
      `${addr}         n/a               n/a        0         n/a                n/a     n/a        n/a        n/a        n/a       n/a    0.00%  n/a                 0.00%    0.00%   0   0:00:00`,
    ].join("\n");
}

const ensurePlaceholder =
  ({ subnet }) =>
  (error) => {
    if (error.startsWith("%ERR-ENOENT: Cannot find subscriber with")) {
      const addr = subnet.replace(/\/.*$/, "");
      return { response: createPlaceholder({ addr }) };
    }
    throw error;
  };

const doSubscriberMetricsRequest = ({
  policy,
  subnet,
  direction,
  lines,
  subscriberGroup,
}) =>
  ensureCIDR(subnet)
    .then((cidrSubnet) => {
      return ifCl.run(
        `show ${statePolicy(policy)} ${stateSubscriber(
          cidrSubnet
        )} ${direction} ${stateSubscriberGroup(
          subscriberGroup
        )} lines ${lines}`
      );
    })
    .then(responseMustContainAtLeastOneRecord(subnet))
    .catch(ensurePlaceholder({ subnet }));

const getThresholds = () => {
  return ifCl.run("show subscriber metrics-thresholds").then((response) => {
    const rows = response.trim("\n").split("\n");
    return rows.reduce((acc, row) => {
      const [metric, value] = row.split(/:\s+/);
      acc[metric] = parseFloat(value);
      return acc;
    }, {});
  });
};

const Report = ({
  policy,
  subnet,
  direction,
  lines,
  subscriberGroup,
  timePeriod,
  metric,
  showDashboard,
  ...otherSettings
}) => {
  const [tableDataRaw, setTableDataRaw] = useState();
  const [tableData, setTableData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [statisticsData, setStatisticsData] = useState({});
  const [colorize, setColorize] = useState(false);
  const debounce = useDebouncer(2100 /*ms*/);
  const isFirstTime = useFirstTime();
  const actions = useContext(ActionsContext);

  const resetData = () => {
    setTableDataRaw();
    setTableData([]);
    setSelected([]);
    setStatisticsData({});
    setThresholds({});
  }

  useEffect(() => {
    resetData();
    doLoad();
    return actions.recv("do-load", () => {
      resetData();
      doLoad();
    });
  }, [policy, subnet, direction, lines, subscriberGroup]);

  const doLoad = () => {
    Promise.all([
      doSubscriberMetricsRequest({
        policy,
        subnet,
        direction,
        lines,
        subscriberGroup,
      }),
      getThresholds(),
    ])
      .then(([{ response: responseMetrics }, responseThresholds]) => {
        const [head, ...rows] = responseMetrics.trim("\n").split("\n");
        const headerAttrs = head.trim().split(/\s+/);
        const headerMap = headerAttrs.reduce((acc, attr, index) => {
          acc[index] = attr;
          return acc;
        }, {});
        const parsedData = rows.reduce(
          (acc, row, indexRow) => {
            const attrs = row.trim().split(/\s+/);
            const item = attrs.reduce(
              (accItem, attr, index) => {
                const attrName = headerMap[index];
                accItem[attrName] = attr;
                if (BOXPLOT_METRICS_SET.has(attrName)) {
                  if (acc[attrName]) {
                    acc[attrName].push(attr);
                  } else {
                    acc[attrName] = [attr];
                  }
                }
                return accItem;
              },
              { id: indexRow }
            );
            acc.rows.push(item);
            return acc;
          },
          { rows: [] }
        );
        setTableDataRaw(responseMetrics);
        setStatisticsData(parsedData);
        setTableData(parsedData.rows);
        setThresholds(responseThresholds);
      })
      .catch((error) => {
        showModalError("Error", error);
      });
  };

  const updateSelected = (newSelection) => {
    const assign = () => {
      if (newSelection.length > SUBSCRIBERS_TO_PLOT_LIMIT) {
        showModalInfo(
          `Cannot select more than ${SUBSCRIBERS_TO_PLOT_LIMIT} subscribers to plot. De-select some subscribers to select new ones.`
        );
        return;
      }
      setSelected(newSelection);
    };
    isFirstTime() ? assign() : debounce(assign);
  };

  const selectedItemsSet = useMemo(() => {
    return new Set(selected.map(item => `${item.addr}:${item.subsId}`));
  }, [selected]);

  const subscribersToPlot = useMemo(() => {
    if (!tableData) return [];
    return tableData.filter((item) => {
      const key = `${item["ADDR"]}:${item["SUBSCRIBER-ID"]}`;
      return selectedItemsSet.has(key);
    });
  }, [tableData, selectedItemsSet]);

  return (
    <>
      <Statistics
        {...otherSettings}
        data={statisticsData}
        thresholds={thresholds}
        setThresholds={setThresholds}
        colorize={colorize}
        setColorize={setColorize}
      />
      <TimeEvolutionMetrics
        direction={direction}
        subscribersToPlot={subscribersToPlot}
        timePeriod={timePeriod}
        metric={metric}
        selected={[...selectedItemsSet]}
      />
      <ListSubscribers
        tableData={tableDataRaw}
        colorize={colorize}
        thresholds={thresholds}
        onPlotSelection={updateSelected}
      />
    </>
  );
};

export default Report;